/**
 * Single source of truth for all funded ventures listing page analytics event
 * names.
 *
 * Names follow the object-action framework: `<l1_product>_<l2_component>_<action>`,
 * snake case, lowercase. L1 is the page (`funded_ventures`), L2 the component on
 * it (`asset_card`, `breadcrumb`, `load_more`).
 *
 * Every asset here is a funded offering, so `asset_type` is constant on this
 * page. It is still sent: /investment-opportunities reports the same events for
 * the same cards, and matching payload shapes let one Segment query cover both
 * and split by `source`.
 */

export const FUNDED_VENTURES_EVENTS = {
  /** An offering card in the grid. Carries `asset_type` + `slug`. */
  ASSET_CARD_CLICKED: 'funded_ventures_asset_card_clicked',
  /** Bookmark toggle on an offering card. Carry `asset_type` + `slug`. */
  ASSET_FOLLOWED: 'funded_ventures_asset_followed',
  ASSET_UNFOLLOWED: 'funded_ventures_asset_unfollowed',
  /** The breadcrumb back to /investment-opportunities. Carries `label`. */
  BREADCRUMB_CLICKED: 'funded_ventures_breadcrumb_clicked',
  /**
   * A further page of the listing was actually requested. Carries `page`, so
   * the funnel shows how deep into an endless scroll people go.
   */
  LOAD_MORE_TRIGGERED: 'funded_ventures_load_more_triggered',
} as const;

export type TFundedVenturesEventName =
  (typeof FUNDED_VENTURES_EVENTS)[keyof typeof FUNDED_VENTURES_EVENTS];

/**
 * The page view, reported through Segment `page()` rather than as a `track`
 * event — every other page in the app does the same, so /funded-ventures shows
 * up in the existing pageview reporting instead of needing its own query.
 */
export const FUNDED_VENTURES_PAGE = {
  category: 'funded-ventures',
  name: 'index',
} as const;

/**
 * `source` for every event on this page, per the event metadata rules.
 *
 * `investor_id`, the other required field, is not passed per event — Segment
 * `identify()` attaches it at login, so every event inherits it.
 */
export const FUNDED_VENTURES_SOURCE = 'funded_ventures';

/** `platform` is web for every event fired from r-client. */
export const FUNDED_VENTURES_PLATFORM = 'web';

/**
 * Flat array of every funded ventures event name. The hook spec
 * (`use-funded-ventures-analytics.spec.ts`) iterates this to assert each name is
 * dispatched by at least one `useFundedVenturesAnalytics` method.
 */
export const ALL_FUNDED_VENTURES_EVENT_NAMES = Object.values(
  FUNDED_VENTURES_EVENTS
);
